/**
 * Beat 11's office: a four-by-five metre crossing, not a new play system.
 *
 * The shell and collision list share one authored set of boxes.  Dressing is
 * separately removable for the census, but the room edge never is.
 */

import * as THREE from 'three';
import { kestnerHatAndCape } from '@/models/env/kestner';
import { surface } from '@/models/materials';
import { getZone, type ZonePalette } from '@/render/palette';
import type { Fixture } from '@/render/lighting';
import { collapse } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';
import { DEFAULT_OUT, tallyForOut } from '@/story/act3/tally';
import { PENCIL_DIGITS } from '@/render/handDigits';

export const OFFICE_ZONE_ID = 'farrowing';
export const OFFICE_EXIT = { minX: -1.60, maxX: -1.15, z: -2.38 } as const;

/**
 * Where she is when the fade comes up: just out of the lee of the filing
 * cabinet, facing the middle of the door's clear chord (D-213).
 *
 * **Not in the gap behind the cabinet, where she came in.** The room is 4 m by
 * 5 m and the follow arm is 1.85 m: from the gap (the old spawn, 1.60, 2.20)
 * the wall behind her clipped the arm to 0.55 m and the frame was a flat dark
 * green, spread 1.9 % at 6 s (audit row 5). From anywhere on the east side, facing
 * the desk or the door, a wall clips it again. So the opening is an authored
 * shot (`OFFICE_OPENING_SHOT`, `src/game/beats/office.ts`) from the gap she
 * came in by, with her a metre ahead of it, where the cabinet no longer hides
 * him.
 *
 * Facing the chord and not the door: the first heading tried (−2.3) ran into
 * the pocket behind the ajar leaf and held her at (−1.87, −2.09). Along this
 * one, W from spawn goes out of the room (`beat-11-office.spec.ts`).
 */
export const OFFICE_SPAWN = {
  x: 1.30,
  y: 0.25,
  z: 0.80,
  facing: Math.atan2((OFFICE_EXIT.minX + OFFICE_EXIT.maxX) / 2 - 1.30, OFFICE_EXIT.z - 0.80),
} as const;

/**
 * Where Kestner's hip axis is, in the room: on the chair, facing the desk (−X).
 *
 * The seat spans x −1.28 … −0.82 and its backrest's inner face is x −0.89; his
 * pelvis reaches 0.17 m behind the hip, so −1.06 perches him forward of the
 * backrest, which is how a man folded over a page sits. `z` is the kneehole's
 * centre and the chair's, so his knees go under the desk between the pedestals.
 * The ledger and the lamp are placed against this, not against the chair.
 */
export const OFFICE_KESTNER_SEAT = { x: -1.06, z: -0.20, yaw: -Math.PI / 2 } as const;

/**
 * The open ledger under his hands: centre, half-extents and top. One statement
 * for the mesh and for `kestner-desk.spec.ts`, which requires both his hands to
 * land over it from the figure the room module actually placed.
 */
/**
 * His map, on the wall of paper: the largest sheet on the east wall, and
 * nothing distinguishes it until she looks (plan, *the room*). Centre and size;
 * the dressing pins a sheet here and the room module aims `observe` at it.
 */
export const OFFICE_WALL_MAP = { x: 1.974, y: 1.42, z: 0.56, width: 0.32, height: 0.24 } as const;

export const OFFICE_LEDGER = { x: -1.50, z: -0.20, halfX: 0.15, halfZ: 0.22, top: 0.764 } as const;

/**
 * Has she crossed the office's one exit plane, within its authored width?
 *
 * `main.ts` used to repeat `OFFICE_EXIT`'s three numbers verbatim in its own
 * crossing predicate (F3, `docs/engine-architecture-audit.md`) — harmless
 * while they agreed, but a later edit to `OFFICE_EXIT` alone would silently
 * leave the runtime transition at the old plane. Same inclusive comparisons
 * as before: `z` is a ceiling (she has left once she reaches or passes it),
 * `x` is a closed range (the doorway is only that wide).
 */
export function isPastOfficeExit(p: { x: number; z: number }): boolean {
  return p.z <= OFFICE_EXIT.z && p.x >= OFFICE_EXIT.minX && p.x <= OFFICE_EXIT.maxX;
}

export const OFFICE = {
  minX: -2,
  maxX: 2,
  minZ: -2.5,
  maxZ: 2.5,
  ceiling: 2.5,
  thickness: 0.12,
  /**
   * How far the landing outside the door reaches, and why there is one.
   *
   * **The doorway is the one hole in this shell and for a day it opened onto
   * nothing.** `office-floor` stops at z −2.5; the opening is in the wall at
   * that plane; so walking out took her off the edge of the world, and
   * `?room=office` — a shipped URL, D-004 — let her fall for ever. The driven
   * crossing came to rest at **x −2.978, z −3.109**, outside the room, with the
   * beat reporting `finished: true`.
   *
   * It was invisible for exactly as long as the door leaf was misplaced: the
   * mirrored leaf lay across the opening and plugged it, so the containment
   * probe measured her stopping at z −2.396 and that reading was of the bug.
   * Fixing the leaf is what let her out.
   *
   * A door opens onto somewhere. This is the half-metre of unlit landing
   * outside it — floor, an end wall and two returns — which is also what the
   * close needs: the beat's fade plays while she is standing on something.
   */
  landingMinZ: -3.05,
  landingMinX: -2,
  landingMaxX: -1.05,
} as const;

export interface OfficeOptions {
  /** The budget control retains only the floor, ceiling, and enclosing walls. */
  readonly dressing?: boolean;
  /** The already-resolved beat-boundary result written on the desk page. */
  readonly tally?: { readonly crossed: number; readonly written: number };
}

const zone: ZonePalette = (() => {
  const found = getZone(OFFICE_ZONE_ID);
  if (!found) throw new Error('office: farrowing zone missing from palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`office: surface ${String(index)} missing from farrowing palette`);
  return colour;
}

function box(name: string, half: HallSolid['half'], pos: HallSolid['pos'], colour: number, kind: HallSolid['kind']): HallSolid {
  return { name, half, pos, colour, kind };
}

/** The shell includes the ajar door's opening; the leaf itself belongs to dress. */
function shellSolids(): HallSolid[] {
  const t = OFFICE.thickness / 2;
  const doorMinX = -2;
  const doorMaxX = -1.15;
  const windowMinX = -0.2;
  const windowMaxX = 0.8;
  // ── The south wall is authored as spans around its two holes ──────────────
  //
  // **The door sits in the corner, so the span west of it is ZERO METRES WIDE**
  // — `(doorMinX − OFFICE.minX) / 2` is exactly 0 — and the first version
  // authored it anyway: a degenerate `BoxGeometry`, a degenerate collider, a
  // draw call and a Rapier body for a wall with no width. Nothing goes red for
  // that; it is not wrong, it is *nothing*, and it renders as nothing.
  //
  // It is filtered rather than deleted because the door has already moved once
  // in this room's short life. If it moves back off the corner, the span comes
  // back on its own instead of being a hole somebody has to notice.
  const authored: HallSolid[] = [
    box('office-floor', { x: 2, y: t, z: 2.5 }, { x: 0, y: -t, z: 0 }, zoneSurface(0), 'mineral'),
    box('office-ceiling', { x: 2, y: t, z: 2.5 }, { x: 0, y: OFFICE.ceiling + t, z: 0 }, zoneSurface(0), 'mineral'),
    box('office-wall-west', { x: t, y: OFFICE.ceiling / 2, z: 2.5 }, { x: OFFICE.minX - t, y: OFFICE.ceiling / 2, z: 0 }, zoneSurface(1), 'mineral'),
    box('office-wall-east', { x: t, y: OFFICE.ceiling / 2, z: 2.5 }, { x: OFFICE.maxX + t, y: OFFICE.ceiling / 2, z: 0 }, zoneSurface(1), 'mineral'),
    // The skirting gap is cosmetic: Rosalie starts inside, and making it an
    // opening would make a 0.30 m-wide escape at the room's most hidden edge.
    box('office-wall-north', { x: 2, y: OFFICE.ceiling / 2, z: t }, { x: 0, y: OFFICE.ceiling / 2, z: OFFICE.maxZ + t }, zoneSurface(1), 'mineral'),
    box('office-south-wall-west', { x: (doorMinX - OFFICE.minX) / 2, y: OFFICE.ceiling / 2, z: t }, { x: (OFFICE.minX + doorMinX) / 2, y: OFFICE.ceiling / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
    box('office-south-wall-door-header', { x: (doorMaxX - doorMinX) / 2, y: (OFFICE.ceiling - 2) / 2, z: t }, { x: (doorMinX + doorMaxX) / 2, y: 2 + (OFFICE.ceiling - 2) / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
    box('office-south-wall-between', { x: (windowMinX - doorMaxX) / 2, y: OFFICE.ceiling / 2, z: t }, { x: (doorMaxX + windowMinX) / 2, y: OFFICE.ceiling / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
    box('office-south-window-sill', { x: (windowMaxX - windowMinX) / 2, y: 1.2 / 2, z: t }, { x: 0.3, y: 1.2 / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
    box('office-south-window-header', { x: (windowMaxX - windowMinX) / 2, y: (OFFICE.ceiling - 1.9) / 2, z: t }, { x: 0.3, y: 1.9 + (OFFICE.ceiling - 1.9) / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
    box('office-south-wall-east', { x: (OFFICE.maxX - windowMaxX) / 2, y: OFFICE.ceiling / 2, z: t }, { x: (windowMaxX + OFFICE.maxX) / 2, y: OFFICE.ceiling / 2, z: OFFICE.minZ - t }, zoneSurface(1), 'mineral'),
  ];
  // The landing outside the door. Shell, never dressing: it is the room's edge,
  // and an edge that can be switched off by `&dress=0` is not one.
  const landingHalfX = (OFFICE.landingMaxX - OFFICE.landingMinX) / 2;
  const landingMidX = (OFFICE.landingMinX + OFFICE.landingMaxX) / 2;
  const landingHalfZ = (OFFICE.minZ - OFFICE.landingMinZ) / 2;
  const landingMidZ = (OFFICE.minZ + OFFICE.landingMinZ) / 2;
  authored.push(
    box('office-landing-floor', { x: landingHalfX, y: t, z: landingHalfZ }, { x: landingMidX, y: -t, z: landingMidZ }, zoneSurface(0), 'mineral'),
    box('office-landing-end', { x: landingHalfX, y: OFFICE.ceiling / 2, z: t }, { x: landingMidX, y: OFFICE.ceiling / 2, z: OFFICE.landingMinZ - t }, zoneSurface(1), 'mineral'),
    box('office-landing-west', { x: t, y: OFFICE.ceiling / 2, z: landingHalfZ }, { x: OFFICE.landingMinX - t, y: OFFICE.ceiling / 2, z: landingMidZ }, zoneSurface(1), 'mineral'),
    box('office-landing-east', { x: t, y: OFFICE.ceiling / 2, z: landingHalfZ }, { x: OFFICE.landingMaxX + t, y: OFFICE.ceiling / 2, z: landingMidZ }, zoneSurface(1), 'mineral'),
  );
  return authored.filter((solid) => solid.half.x > 0 && solid.half.y > 0 && solid.half.z > 0);
}

/** Every dressed thing that can stop Rosalie is listed here as well as drawn. */
function dressingSolids(): HallSolid[] {
  return [
    box('office-cabinet', { x: 0.235, y: 0.66, z: 0.31 }, { x: 1.00, y: 0.66, z: 1.95 }, zoneSurface(2), 'metal'),
    box('office-desk-top', { x: 0.35, y: 0.0225, z: 0.8 }, { x: -1.60, y: 0.7275, z: -0.20 }, zoneSurface(2), 'organic'),
    box('office-desk-pedestal-north', { x: 0.35, y: 0.365, z: 0.175 }, { x: -1.60, y: 0.365, z: -0.825 }, zoneSurface(2), 'organic'),
    box('office-desk-pedestal-south', { x: 0.35, y: 0.365, z: 0.175 }, { x: -1.60, y: 0.365, z: 0.425 }, zoneSurface(2), 'organic'),
    box('office-chair-seat', { x: 0.23, y: 0.045, z: 0.23 }, { x: -1.05, y: 0.45, z: -0.20 }, zoneSurface(2), 'organic'),
    // ── The backrest is on the +X side, because that is the side he is NOT ────
    //
    // It was authored at x −1.245, which is the seat's DESK edge: the chair
    // faced away from the desk, and the frame from his seated eye had the
    // backrest between the camera and the page. The seat spans x −1.28…−0.82,
    // so the back belongs at its far edge and he sits looking −X, at the desk.
    box('office-chair-back', { x: 0.035, y: 0.225, z: 0.23 }, { x: -0.855, y: 0.675, z: -0.20 }, zoneSurface(3), 'organic'),
    {
      // ── The leaf is placed from its HINGE, and the first version was not ───
      //
      // A rotated box is positioned at its CENTRE, so the centre is the hinge
      // plus half a leaf-length along the leaf's own direction — not the hinge.
      // Authored at x −1.993 (the hinge, near enough) the leaf spanned
      // x −2.361…−1.625 with **0.36 m of it buried in the west wall**, and
      // nothing went red: the crossing still passed, because the part of the
      // opening it failed to cover is exactly the part the exit trigger is in.
      // Only a frame could have said so, and that is why one was taken.
      //
      //   centre = hinge + half × (cos yaw, −sin yaw)
      //          = (−2 + 0.425 cos 45°, −2.5 + 0.425 sin 45°)
      //          = (−1.6995, −2.1995)   with yaw = −45°
      //
      // **The sign on the z term is not a detail and it was wrong here once.**
      // `solids.ts` bakes the pose with `geometry.rotateY(yaw)`, and Three's
      // rotateY sends local (+x, 0, 0) to `(x cos θ, −x sin θ)` — the z offset
      // is NEGATIVE for a positive yaw. Authored at +45° with this same centre,
      // the leaf came out mirrored: hinged in the middle of the opening and
      // lying back against the west wall, leaving 0.37 m at the exit plane
      // instead of 0.73. Found on independent review, after a comment in this
      // file had asserted the other convention in arithmetic that looked
      // checked.
      //
      // ── And it stands at 45°, not the 30° the plan first said ──────────────
      //
      // The gap she actually goes through is not the opening, it is the CHORD
      // from the leaf's free edge to the far jamb — and it is diagonal:
      //
      //     30°  free edge (−1.264, −2.075)   chord **0.44 m**
      //     45°  free edge (−1.399, −1.899)   chord **0.65 m**
      //     60°  free edge (−1.575, −1.764)   chord **0.85 m**
      //
      // She is 0.32 m tall and about 0.35 m wide, so 30° is a 0.44 m diagonal
      // slot for a 0.35 m animal — passable on paper and fiddly to walk, which
      // is the wrong kind of difficulty for a beat with no fail state. 45° is
      // what a man who came in and did not shut the door behind him leaves, it
      // reads unmistakably as ajar rather than as open, and it gives her 0.65 m.
      ...box('office-door-leaf', { x: 0.425, y: 1, z: OFFICE.thickness / 2 }, { x: -1.6995, y: 1, z: -2.1995 }, zoneSurface(2), 'organic'),
      // Same authored pose in the mesh and Rapier. An AABB here would close the
      // very mouth the 30-degree calculation leaves Rosalie.
      yaw: -Math.PI / 4,
    },
  ];
}

export function officeSolids(options: OfficeOptions = {}): HallSolid[] {
  return options.dressing === false ? shellSolids() : [...shellSolids(), ...dressingSolids()];
}

function meshBox(size: readonly [number, number, number], at: readonly [number, number, number], kind: HallSolid['kind'], colour: number): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), surface(kind, colour));
  mesh.position.set(...at);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * The column, oriented for HIM. He faces −X, so his line of writing runs
 * along −Z (his right) and down his page is +X (toward him). A column written
 * along the room's +X would be sideways to the man writing it, which is the
 * kind of error that reads at once in a close frame and never in a census.
 */
const TALLY_GLYPH_HEIGHT = 0.021;
const TALLY_STROKE = 0.0019;
const TALLY_ROW_PITCH = 0.036;
const TALLY_ADVANCE = 0.018;
/**
 * Where the first row starts, and it is the one clear strip on the page. His
 * forearms run along X and land at z ≈ −0.28 (the pencil hand) and z ≈ −0.10
 * (the left, holding the page flat), measured off the placed figure; between
 * them z −0.23…−0.15 is clear from the far edge to the near one. The first
 * placements put the column under one hand and then the other.
 */
const TALLY_ORIGIN = { x: OFFICE_LEDGER.x - OFFICE_LEDGER.halfX + 0.01, z: OFFICE_LEDGER.z + 0.045 } as const;

function tallyMesh(tally: NonNullable<OfficeOptions['tally']>): THREE.Group {
  const group = new THREE.Group();
  const glyphs = ['238', '231', '226', String(tally.crossed), String(tally.written)];
  const graphite = surface('mineral', zoneSurface(3));
  // Page space → world: `u` runs along −Z, `v` along +X. Flat strokes, 0.6 mm
  // proud of the page — a pencil mark has no height, and the first version's
  // 3 mm bars were visibly standing up at the shot's low angle.
  const y = OFFICE_LEDGER.top + 0.0008;
  const toWorld = (u: number, v: number): THREE.Vector2 => new THREE.Vector2(TALLY_ORIGIN.x + v, TALLY_ORIGIN.z - u);
  const stroke = (a: THREE.Vector2, b: THREE.Vector2, width: number): void => {
    const length = a.distanceTo(b) + width;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(length, 0.0004, width), graphite);
    mesh.position.set((a.x + b.x) / 2, y, (a.y + b.y) / 2);
    mesh.rotation.y = -Math.atan2(b.y - a.y, b.x - a.x);
    group.add(mesh);
  };
  for (const [row, glyph] of glyphs.entries()) {
    for (const [column, digit] of [...glyph].entries()) {
      for (const line of PENCIL_DIGITS[digit] ?? []) {
        for (let i = 1; i < line.length; i += 1) {
          const from = line[i - 1];
          const to = line[i];
          if (from === undefined || to === undefined) continue;
          const u = column * TALLY_ADVANCE;
          const v = row * TALLY_ROW_PITCH;
          stroke(
            toWorld(u + from[0] * TALLY_GLYPH_HEIGHT, v + from[1] * TALLY_GLYPH_HEIGHT),
            toWorld(u + to[0] * TALLY_GLYPH_HEIGHT, v + to[1] * TALLY_GLYPH_HEIGHT),
            TALLY_STROKE,
          );
        }
      }
    }
  }
  // The old figure is struck, not erased: a correction by somebody who
  // reconciles stock. One ruled line through row 4, a little past both ends,
  // and no wider than the figure, because a page-wide bar joined the rows
  // into one mark in the desk shot.
  const struckV = 3 * TALLY_ROW_PITCH + TALLY_GLYPH_HEIGHT * 0.55;
  stroke(toWorld(-0.004, struckV + 0.002), toWorld(2 * TALLY_ADVANCE + 0.6 * TALLY_GLYPH_HEIGHT + 0.004, struckV - 0.002), TALLY_STROKE * 1.2);
  collapse(group, 'office-tally-merged');
  const merged = group.getObjectByName('office-tally-merged');
  if (merged === undefined) throw new Error('office: tally merge produced no mesh');
  merged.userData['glyphs'] = glyphs;
  return group;
}

/**
 * Marks on a pinned sheet, as flat strokes on its room-facing face. Points are
 * in the sheet's own unit square: `u` 0…1 left to right as seen from the room
 * (+Z), `v` 0…1 top to bottom. Children of the sheet, so they take its pitch,
 * and merged with the dressing, so they cost nothing of their own.
 */
function addSheetStrokes(
  sheet: THREE.Mesh,
  width: number,
  height: number,
  lines: readonly (readonly (readonly [number, number])[])[],
  material: THREE.Material,
): void {
  const face = -0.0065;
  const at = (u: number, v: number): THREE.Vector2 => new THREE.Vector2((u - 0.5) * width, (0.5 - v) * height);
  for (const line of lines) {
    for (let i = 1; i < line.length; i += 1) {
      const from = line[i - 1];
      const to = line[i];
      if (from === undefined || to === undefined) continue;
      const a = at(from[0], from[1]);
      const b = at(to[0], to[1]);
      const stroke = new THREE.Mesh(new THREE.BoxGeometry(0.0006, 0.0022, a.distanceTo(b) + 0.0022), material);
      stroke.position.set(face, (a.y + b.y) / 2, (a.x + b.x) / 2);
      stroke.rotation.x = -Math.atan2(b.y - a.y, b.x - a.x);
      sheet.add(stroke);
    }
  }
}

/** His plan in miniature: border, margin, the field fence with its gap, the ditch, and a dashed line. */
const WALL_MAP_STROKES: readonly (readonly (readonly [number, number])[])[] = [
  [[0.06, 0.08], [0.94, 0.08], [0.94, 0.92], [0.06, 0.92], [0.06, 0.08]],
  [[0.22, 0.08], [0.22, 0.92]],
  [[0.30, 0.16], [0.90, 0.16], [0.90, 0.70], [0.30, 0.70], [0.30, 0.16]],
  [[0.30, 0.36], [0.62, 0.36]], [[0.68, 0.36], [0.90, 0.36]],
  [[0.26, 0.76], [0.90, 0.76]], [[0.26, 0.80], [0.90, 0.80]],
  // Her line, dashed: in at the gap, down to the culvert.
  ...Array.from({ length: 7 }, (_, i): readonly (readonly [number, number])[] => {
    const t0 = i / 7;
    const t1 = t0 + 0.5 / 7;
    const p = (t: number): readonly [number, number] => [0.66 - t * 0.34, 0.20 + t * 0.58];
    return [p(t0), p(t1)];
  }),
  // Four dates in the margin, as ticks: at wall distance they are marks, not figures.
  [[0.10, 0.18], [0.18, 0.18]], [[0.10, 0.26], [0.18, 0.26]], [[0.10, 0.34], [0.18, 0.34]], [[0.10, 0.42], [0.18, 0.42]],
];

/** A rosette and ruled lines: a certificate as a mark. Never letters (§7). */
const CERTIFICATE_STROKES: readonly (readonly (readonly [number, number])[])[] = [
  Array.from({ length: 13 }, (_, i): readonly [number, number] => {
    const a = (i / 12) * Math.PI * 2;
    return [0.24 + Math.cos(a) * 0.12, 0.40 + Math.sin(a) * 0.17];
  }),
  [[0.20, 0.56], [0.15, 0.86], [0.21, 0.80], [0.25, 0.88], [0.26, 0.57]],
  [[0.46, 0.26], [0.88, 0.26]], [[0.46, 0.40], [0.88, 0.40]], [[0.46, 0.54], [0.80, 0.54]], [[0.46, 0.72], [0.70, 0.72]],
];

function dressing(tally: NonNullable<OfficeOptions['tally']>): THREE.Group {
  const group = new THREE.Group();
  group.name = 'office-dressing';
  const mineral = zoneSurface(1);
  const organic = zoneSurface(2);
  const metal = zoneSurface(3);

  // Drawer seams and chair legs are visual detail only. The cabinet, desk,
  // chair and door themselves are rendered from `officeSolids()` so collision
  // cannot drift away from their visible dimensions.
  for (const y of [0.44, 0.88]) group.add(meshBox([0.475, 0.018, 0.50], [1, y, 1.635], 'mineral', mineral));
  for (const x of [-1.22, -0.88]) for (const z of [-0.37, -0.03]) group.add(meshBox([0.035, 0.45, 0.035], [x, 0.225, z], 'metal', metal));

  // ── The lamp, which was a stick until somebody looked at a frame ──────────
  //
  // The first version was a 0.37 m pole with a 0.10 m cone on top, standing on
  // the desk pedestal, and it read as a signpost. Worse, the light sat 0.24 m
  // off the west wall, so what it lit was **a perfect circle on the wall behind
  // it** while the desk top stayed in shadow — the exact inverse of the one
  // thing this fixture is for. `docs/act-3-plan.md`: *"the lamp is below his
  // eyeline, so it lights the page and his hands."* Every gate was green for it.
  //
  // So it is an anglepoise now: a short stem, an arm that reaches OUT over the
  // desk, and a shade with a real mouth on the end of that arm. The shape is
  // what puts the bulb over the page instead of against the plaster.
  const lamp = new THREE.Group();
  lamp.name = 'office-desk-lamp';
  lamp.add(meshBox([0.15, 0.022, 0.15], [-1.82, 0.762, 0.30], 'metal', metal));
  lamp.add(meshBox([0.035, 0.30, 0.035], [-1.82, 0.923, 0.30], 'metal', metal));
  const arm = meshBox([0.34, 0.028, 0.028], [-1.665, 1.035, 0.30], 'metal', metal);
  arm.rotation.z = -0.42;
  lamp.add(arm);
  const shade = new THREE.Mesh(new THREE.ConeGeometry(0.15, 0.17, 14, 1, true), surface('metal', metal));
  // **`ConeGeometry` already has its mouth down** — the apex is at +height/2.
  // Rotating it by PI about X therefore turns the shade UP, and the frame that
  // came back was a martini glass on a desk. Only the tip toward the page is
  // authored here.
  shade.position.set(-1.52, 1.045, 0.30);
  shade.rotation.set(0, 0, 0.38);
  shade.castShadow = true;
  shade.receiveShadow = true;
  lamp.add(shade);
  group.add(lamp);

  // The wall of paper. Most sheets stay blank: a wall of legible notices is a
  // wall of words, and §7 has none. Two carry marks (beat 11 stage 4):
  //  - his map, which from the floor is one more ruled sheet — *nothing
  //    distinguishes it until she looks* — and under `Q` becomes the overlay;
  //  - the farm's welfare certificate: a rosette and ruled lines, pinned on the
  //    same wall as the map of her routes and **neither one ironic**
  //    (`docs/art-direction.md`, *clean face*). A mark, not a paragraph.
  const sheets: readonly (readonly [number, number, number, number, number])[] = [
    [1.974, 1.55, -1.38, 0.30, 0.22], [1.974, 1.32, -0.88, 0.24, 0.30], [1.974, 1.68, -0.48, 0.34, 0.20],
    [1.974, 1.24, -0.08, 0.26, 0.18], [1.974, 1.72, 0.20, 0.22, 0.28], [OFFICE_WALL_MAP.x, OFFICE_WALL_MAP.y, OFFICE_WALL_MAP.z, OFFICE_WALL_MAP.width, OFFICE_WALL_MAP.height],
    [1.974, 1.88, -1.00, 0.20, 0.16], [1.974, 1.12, -1.58, 0.18, 0.14], [1.974, 1.92, 0.74, 0.18, 0.16],
  ];
  const CERTIFICATE_SHEET = 3;
  const MAP_SHEET = 5;
  const graphite = surface('mineral', metal);
  for (const [index, [x, y, z, width, height]] of sheets.entries()) {
    const sheet = meshBox([0.012, height, width], [x, y, z], 'organic', organic);
    // These are paper sheets, not a tiled wall. A slight pitch keeps their
    // edges human-placed.
    sheet.rotation.x = (index % 3 - 1) * 0.045;
    if (index === MAP_SHEET) addSheetStrokes(sheet, width, height, WALL_MAP_STROKES, graphite);
    if (index === CERTIFICATE_SHEET) addSheetStrokes(sheet, width, height, CERTIFICATE_STROKES, graphite);
    group.add(sheet);
  }


  // ── His hat and cape, on a hook by the door (beat 11 stage 2) ─────────────
  //
  // He is at the desk without them (`kestnerSeated.ts`), and the plan wants
  // them *"on a hook by the door, still wet"* so that the brim — the mark he is
  // read by at 64 px — is still in the room. On the west wall, north of the
  // leaf's swing, at a man's shoulder height: she passes under it on the way
  // out. The cape's hem is 1.2 m up, so none of it is anything she walks into.
  const hook = meshBox([0.05, 0.03, 0.03], [OFFICE.minX + 0.025, 1.72, -1.55], 'metal', metal);
  group.add(hook);
  const coat = kestnerHatAndCape();
  coat.position.set(OFFICE.minX + 0.01, 1.70, -1.55);
  coat.rotation.y = Math.PI / 2;
  group.add(coat);

  // ── The ledger, open where his hands are ──────────────────────────────────
  //
  // Under his pencil and his flat left forearm (`kestnerSeated.ts` HAND_REST,
  // seated at `OFFICE_KESTNER_SEAT`). Blank, like the wall: the tally is
  // stage 3's and it arrives as marks on this page, not as a second page.
  // The palest thing on the desk, because it is what the lamp is for.
  const ledger = new THREE.Mesh(
    new THREE.BoxGeometry(OFFICE_LEDGER.halfX * 2, 0.014, OFFICE_LEDGER.halfZ * 2),
    surface('organic', 0xa49d8b),
  );
  ledger.position.set(OFFICE_LEDGER.x, OFFICE_LEDGER.top - 0.007, OFFICE_LEDGER.z);
  ledger.castShadow = true;
  ledger.receiveShadow = true;
  group.add(ledger);

  // ── D-034: static scenery goes through `collapse()` ───────────────────────
  //
  // Nineteen meshes — two drawer seams, four chair legs, three lamp parts, nine
  // sheets and a windowpane — measured **62 draw calls for 936 triangles**. (The
  // pane is gone now; see below.)
  // That is 3.3 calls each and it is the ratio D-034 exists to stop: every
  // unique mesh is paid for three times over in the shadow pass, the normal
  // prepass and the beauty pass, so a room made of small props costs its calls
  // by COUNT and not by size. The wool shed is 161 calls for a hundred times
  // the geometry.
  //
  // **Nothing in here moves.** The lamp does not flicker, the sheets do not
  // stir, and beat 11's only moving thing is Kestner, who is a separate group
  // that stage 2 adds beside this one. That is what makes the collapse safe:
  // `collapse` is what took her mother's head off when it was run over a live
  // hierarchy, and the rule it left behind is that a live node must be a pivot
  // (`collapseRigged`) or must not be inside the merge at all.
  collapse(group, 'office-dressing-merged');
  group.add(tallyMesh(tally));

  // ── There is no windowpane, and that is the fix rather than a cut ────────
  //
  // `docs/act-3-plan.md` wants *"a window with the yard behind it in the dark"*.
  // A pane was authored for it and came back **pale** three times: at
  // `zone.shadow`, then at a quarter of it, then at six per cent of it. The
  // reason is the material and not the colour — the toon ramp's shadow band is
  // deliberately tinted toward the zone fill and never approaches black
  // (`TOON_RAMP.shadowFillMix`), so a lit surface in this room has a floor it
  // cannot go under whatever base colour it is given.
  //
  // **The opening already is the window.** The wall is authored as spans around
  // a hole, and what is behind the hole is the scene background — which this
  // room sets to `zone.shadow × 0.08` and which is not a surface, so the ramp
  // never touches it. Deleting the pane produced the black rectangle that three
  // colour edits could not, and cost two draw calls less than having one.
  //
  // Identified by moving the pane 10 m up and re-shooting, because the frame
  // before that read as "unchanged" and it was not: the difference between a
  // pale pane and a slightly-less-pale pane is not a thing eyes are good at.
  return group;
}

export function officeFixtures(): Fixture[] {
  // Under the shade's mouth and OVER the desk, not against the wall. Range is
  // short on purpose: this room's corners are supposed to be unlit, and a point
  // light with a long range fills a 4 x 5 m box evenly, which is what the first
  // pass did. Measured against the sweep rather than reasoned — see below.
  // ── Why this is 0.27 m above the desk and 0.50 m off the wall ─────────────
  //
  // There is no `spot` in `Fixture` — `'point' | 'batten'` — so the only aiming
  // this fixture has is WHERE IT IS. Two frames were spent learning what that
  // costs. At y 0.905 the bulb sat 0.15 m over the desk and grazed it, so the
  // desk took a shallow lambert while the wall 0.24 m behind took a
  // near-perpendicular one: the brightest thing in the room was a circle of
  // plaster. Lifting it to 1.02 and pulling it out to x −1.50 makes the desk
  // the near-perpendicular surface instead, which is what a desk lamp is.
  //
  // Range 2.0, not 3.1. A point light with a long range fills a 4 x 5 m box
  // evenly and this room's corners are supposed to be unlit.
  return [{
    kind: 'spot',
    name: 'office-desk-lamp-light',
    position: { x: -1.50, y: 1.02, z: 0.30 },
    // ── Aimed at the LEDGER, where his hands are (stage 2) ──────────────────
    //
    // It was aimed at z 0.10, the middle of the empty desk, and with him in
    // the chair the pool fell beside his hands rather than on them: the frame
    // was a lit rectangle of desk and a dark man next to it. D-187's lever is
    // *"a face whose hands are the brightest thing in frame is a man working"*,
    // so the target is the page under his pencil and pinning forearm
    // (`OFFICE_KESTNER_SEAT`, `kestnerSeated.ts` HAND_REST). The head stays
    // where it is; an anglepoise angled across the page is what one is for.
    target: { x: -1.56, y: 0.75, z: -0.12 },
    angle: 0.6,
    penumbra: 0.5,
    intensity: 2.6,
    range: 2.0,
  }];
}

export function officeAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [];
}

export function createOffice(options: OfficeOptions = {}): THREE.Group {
  const room = new THREE.Group();
  room.name = 'office';
  room.add(solidMeshes(officeSolids(options), 'office-shell'));
  if (options.dressing !== false) room.add(dressing(options.tally ?? tallyForOut(DEFAULT_OUT)));
  return room;
}
